import { useRef, useState } from "react";
import { getAuthProviderLabel } from "../../../auth/providerCapabilities";
import {
  clearLastUsedAuthProvider,
} from "../../../auth/components/ProviderButton";
import {
  navigateToProviderAuthorization,
} from "../../../auth/oauthStart";
import {
  startProviderLink,
  startStepUpEmail,
  unlinkAuthIdentity,
  verifyStepUpEmail,
} from "../../../services/api";
import { Button, Card } from "../../../design-v2";
import SettingsSection from "../SettingsSection";
import ConnectedAccountRow from "./ConnectedAccountRow";
import StepUpVerification from "./StepUpVerification";

const LAST_AUTH_METHOD_COPY =
  "Google is currently your only way to sign in. Verify your email first so you keep a way back in.";

function formatProviderLabel(providerId) {
  const knownLabel = getAuthProviderLabel(providerId);
  if (knownLabel) {
    return knownLabel;
  }

  return String(providerId || "Provider")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function blockedReasonCopy(provider, label) {
  if (provider.connectBlockedReason === "PROVIDER_UNAVAILABLE") {
    return `${label} sign-in is temporarily unavailable.`;
  }
  if (provider.disconnectBlockedReason === "LAST_AUTH_METHOD") {
    return LAST_AUTH_METHOD_COPY;
  }
  return null;
}

export default function ConnectedAccountsSection({
  providers = [],
  auth = {},
  capabilities = {},
  onAccountChange,
  onRefreshSettings,
}) {
  const [pendingProvider, setPendingProvider] = useState(null);
  const [actionError, setActionError] = useState("");
  const [stepUp, setStepUp] = useState(null);
  const resumedActionRef = useRef(null);
  const actionsVisible = capabilities.canLinkProviders !== false;

  async function openStepUp(action) {
    if (auth.stepUp?.available === false) {
      setActionError("Email verification is unavailable for this account.");
      return;
    }
    setPendingProvider(action.providerId);
    setActionError("");
    try {
      const response = await startStepUpEmail();
      resumedActionRef.current = action;
      setStepUp({ sentTo: response.sentTo });
    } catch (error) {
      setActionError(
        error?.code === "STEP_UP_UNAVAILABLE"
          ? "Email verification is unavailable for this account."
          : error?.message || "Unable to start verification."
      );
    } finally {
      setPendingProvider(null);
    }
  }

  async function runProtectedAction(action, { resumed = false } = {}) {
    setPendingProvider(action.providerId);
    setActionError("");
    try {
      if (action.type === "connect") {
        const response = await startProviderLink(action.providerId);
        navigateToProviderAuthorization({
          providerId: action.providerId,
          authorizationUrl: response.authorizationUrl,
        });
        return;
      }

      const response = await unlinkAuthIdentity(action.providerId);
      clearLastUsedAuthProvider(action.providerId);
      onAccountChange?.(response.account);
    } catch (error) {
      if (error?.code === "REAUTH_REQUIRED" && !resumed) {
        setPendingProvider(null);
        await openStepUp(action);
        return;
      }
      if (error?.code === "PROVIDER_DISABLED") {
        await onRefreshSettings?.();
      }
      setActionError(
        error?.code === "PROVIDER_DISABLED"
          ? "This provider is temporarily unavailable."
          : error?.code === "LAST_AUTH_METHOD"
          ? LAST_AUTH_METHOD_COPY
          : error?.code === "IDENTITY_CONFLICT"
            ? "That account is already connected elsewhere."
            : error?.message || "Unable to update this connection."
      );
    } finally {
      setPendingProvider(null);
    }
  }

  async function requestAction(action) {
    if (!auth.isRecentlyAuthenticated) {
      await openStepUp(action);
      return;
    }
    await runProtectedAction(action);
  }

  async function handleStepUpVerify(code) {
    await verifyStepUpEmail(code);
    const action = resumedActionRef.current;
    resumedActionRef.current = null;
    setStepUp(null);
    if (action) {
      await runProtectedAction(action, { resumed: true });
    }
  }

  return (
    <SettingsSection title="Connected Accounts">
      {providers.length > 0 ? (
        <Card padding="none">
          {providers.map((provider) => {
            const label = formatProviderLabel(provider.id);
            const explanation = blockedReasonCopy(provider, label);
            const isPending = pendingProvider === provider.id;

            return (
              <ConnectedAccountRow
                key={provider.id}
                providerId={provider.id}
                label={label}
                identifier={provider.email || null}
                connected={Boolean(provider.connected)}
                busy={isPending}
                actionsVisible={actionsVisible}
                canConnect={Boolean(provider.canConnect)}
                canDisconnect={Boolean(provider.canDisconnect)}
                onConnect={() => requestAction({
                  type: "connect",
                  providerId: provider.id,
                })}
                onDisconnect={() => requestAction({
                  type: "unlink",
                  providerId: provider.id,
                })}
                secondaryAction={
                  provider.connected &&
                  provider.disconnectBlockedReason === "LAST_AUTH_METHOD" &&
                  auth.stepUp?.available ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="lz-settings-inline-action"
                      disabled={isPending}
                      onClick={() => openStepUp({
                        type: "unlink",
                        providerId: provider.id,
                      })}
                    >
                      Verify email to unlink
                    </Button>
                  ) : null
                }
                note={actionsVisible ? explanation : null}
              />
            );
          })}
        </Card>
      ) : null}

      {actionError ? (
        <p className="lz-settings-error" role="alert">
          {actionError}
        </p>
      ) : null}
      {stepUp ? (
        <StepUpVerification
          sentTo={stepUp.sentTo}
          onVerify={handleStepUpVerify}
          onCancel={() => {
            resumedActionRef.current = null;
            setStepUp(null);
          }}
        />
      ) : null}
    </SettingsSection>
  );
}

export { LAST_AUTH_METHOD_COPY, formatProviderLabel };
